import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, Spacer } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import IconChevronRightMedium from "@amzn/stencil-react-icons/icons/icon-chevron-right-medium";
import IconTickMedium from "@amzn/stencil-react-icons/icons/icon-tick-medium";
import styled from "@emotion/styled";
import { PropsWithChildren } from "react";
import { FormattedMessage, IntlShape } from "react-intl";
type VerificationOptionRow = {
  stepi18nId: string;
  stepOnClick?: () => void;
  stepIcon?: JSX.Element;
  optionIndex: number;
  intlProvider: IntlShape;
  stepCompleted?: boolean;
  expandBtnIconAlti18nId: string;
  stepI18nValues?: Record<string, any>;
  disabled?: boolean;
};

const EnabledWrapper = styled.div`
  &:hover {
    background: #f1f4f7;
    cursor: pointer;
  }
`;

const DisabledWrapper = styled.div`
  opacity: 0.6;
`;

const ShiftUp22Px = styled.div`
  margin-top: -25px;
`;

const BorderTop = styled.div`
  border-top: 1px solid #e8eaee;
`;

const BorderTopBottom = styled.div`
  border-top: 1px solid #e8eaee;
  border-bottom: 1px solid #e8eaee;
`;

const Wrapper = (props: PropsWithChildren<{ disable?: boolean }>) => {
  if (props.disable) {
    return <DisabledWrapper>{props.children}</DisabledWrapper>;
  }
  return <EnabledWrapper>{props.children}</EnabledWrapper>;
};

export const VerificationOptionRow = ({
  stepi18nId,
  stepIcon,
  stepOnClick,
  intlProvider,
  stepCompleted,
  optionIndex,
  expandBtnIconAlti18nId,
  stepI18nValues,
  disabled,
}: VerificationOptionRow): JSX.Element => {
  const innerContent = (
    <Wrapper disable={disabled}>
      <Row
        key={`${stepi18nId}-row`}
        dataTestId={`${stepi18nId}-row`}
        onClick={!disabled && stepOnClick ? stepOnClick : undefined}
        justifyContent="space-between"
        padding={{ top: "20px", left: "16px", bottom: "20px", right: "16px" }}
      >
        <Col>
          <Spacer height="S200" />
          <Row>
            <>
              {stepIcon}
              <Spacer width="S300" />
              <Text
                fontWeight="regular"
                color="neutral90"
                dataTestId={stepi18nId}
              >
                <FormattedMessage id={stepi18nId} values={stepI18nValues} />
              </Text>
            </>
          </Row>
        </Col>
        <Col>
          <Row>
            {!stepCompleted && (
              <Button
                disabled={disabled}
                icon={
                  <IconChevronRightMedium
                    color="neutral90"
                    title={intlProvider.formatMessage(
                      {
                        id: expandBtnIconAlti18nId,
                      },
                      stepI18nValues
                    )}
                  />
                }
                variant={ButtonVariant.Tertiary}
              />
            )}
            {stepCompleted && (
              <IconTickMedium
                title={intlProvider.formatMessage({ id: "icon-title" })}
                color="neutral90"
                dataTestId="iconTickDataTestId"
                margin={{ right: "S300" }}
              />
            )}
          </Row>
        </Col>
      </Row>
    </Wrapper>
  );

  if (optionIndex > 0) {
    // Not first row
    return (
      // Row height or gap options aren't helping so manually shift the rows a few px up.
      <ShiftUp22Px>
        {/*Add a top and bottom border. Normally this is handled by a <Hr /> element but that's causing bad formatting for flex display. */}
        <BorderTopBottom>{innerContent}</BorderTopBottom>
      </ShiftUp22Px>
    );
  } else {
    // First row
    return <BorderTop>{innerContent}</BorderTop>;
  }
};
